import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import toast from "react-hot-toast";
import validSupergitHIS from "../../api/validSupergitHIS";
import TableLoading from "../../components/Shared/TableLoading";
import CustomTable from "../../components/Shared/CustomTable";
import CustomPagination from "../../components/Shared/CustomPagination";
import SharedTooltip from "../../components/Shared/SharedTooltip";
import HisDepositInvoiceFilters from "./HisDepositInvoiceFilters";

function HisDepositInvoice() {
  const { t } = useTranslation();
  const currentDate = moment().format("YYYY-MM-DD");
  const firstDayOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");

  const [filters, setFilters] = useState({
    start_date: firstDayOfCurrentMonth,
    end_date: currentDate,
    document_id: "",
    filters: {
      file_number: "",
      created_by: "",
      payment_method: "",
      invoice_number: "",
    },
    page: 1,
    query: "",
    per_page: 10,
  });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async (payload = filters) => {
    setLoading(true);
    try {
      const response = await validSupergitHIS.post(
        "his/depositinvoices",
        payload
      );
      setData(response?.data || {});
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to fetch deposit invoices"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onPageChange = (page, rowsPerPage) => {
    const newFilters = { ...filters, page, per_page: rowsPerPage };
    setFilters(newFilters);
    fetchData(newFilters);
  };

  const handleAddFilters = async () => {
    if (filters?.start_date && !filters?.end_date) {
      toast.error(t("Please select an end date"));
      return;
    }
    if (filters?.end_date && !filters?.start_date) {
      toast.error(t("Please select a start date"));
      return;
    }
    const payload = { ...filters, page: 1 };
    setFilterLoading(true);
    await fetchData(payload);
    setFilterLoading(false);
    setShowFilters(true);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      start_date: firstDayOfCurrentMonth,
      end_date: currentDate,
      document_id: "",
      filters: {
        file_number: "",
        created_by: "",
        payment_method: "",
        invoice_number: "",
      },
      page: 1,
      query: "",
      per_page: 10,
    };
    setFilters(resetFilters);
    fetchData(resetFilters);
    setShowFilters(false);
  };

  const formatAmount = (value) => {
    if (value == null) return 0;
    return value % 1 !== 0 ? value.toFixed(2) : value;
  };

  const columnsData = [
    {
      field: "invoice_number",
      header: t("Invoice Number"),
      style: { width: "8rem" },
    },
    {
      field: "created_at",
      header: t("Created At"),
      body: (rowData) =>
        moment(rowData?.created_at).format("DD-MM-YYYY HH:mm a"),
    },
    { field: "patient_name", header: t("Patient Name") },
    { field: "file_number", header: t("File No") },
    { field: "document_id", header: t("Document ID") },
    {
      field: "deposit_amount",
      header: t("Deposit Amount"),
      body: (el) =>
        formatAmount(
          el?.deposit_amount || el?.deposit_detail?.deposit_amount || 0
        ),
    },
    {
      field: "last_balance",
      header: t("Previous Balance"),
      body: (el) => formatAmount(el?.last_balance || 0),
    },
    {
      field: "total_balance",
      header: t("Total Amount"),
      body: (el) => formatAmount(el?.total_balance || 0),
    },
    {
      field: "payment_method",
      header: t("Payment Method"),
      body: (el) => {
        if (el?.deposit_detail?.payment_detail?.[0]?.payment_method) {
          return el.deposit_detail.payment_detail[0].payment_method;
        }

        if (el?.deposit_detail?.payment_option) {
          return el.deposit_detail.payment_option;
        }

        return "N/A";
      },
    },
    { field: "created_by", header: t("Created By") },
    // { field: "patient_status", header: t("Status") },
  ];

  return (
    <div>
      <div
        className={
          "flex justify-between flex-col gap-3 sm:gap-0 sm:flex-row mb-3"
        }
      >
        <SharedTooltip
          content={t("Keep_track_of_your_invoices")}
          position="left"
          id="Items"
        />
        <span
          data-tooltip-id="Items"
          className="font-semibold text-black/70 text-xl mt-2"
        >
          {t("Deposit Invoices")}
          <span className="italic text-lg ml-1">
            <span className="text-xs font-bold italic">
              ( {data?.totalRecords || 0} {t("Entries")})
            </span>
          </span>
        </span>
      </div>
      <HisDepositInvoiceFilters
        filters={filters}
        setFilters={setFilters}
        handleAddFilters={handleAddFilters}
        filterLoading={filterLoading}
        handleResetFilters={handleResetFilters}
        showFilters={showFilters}
      />
      <CustomTable
        data={data?.data || []}
        isLoading={loading || filterLoading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
      />
      <CustomPagination
        currentPage={data?.page || 1}
        rowsPerPage={data?.per_page || 10}
        totalRecords={Math.ceil(
          (data?.totalRecords || 0) / (data?.per_page || 1)
        )}
        totalRecord={data?.totalRecords || 0}
        onPageChange={onPageChange}
      />
    </div>
  );
}

export default HisDepositInvoice;
